/**
 * RunMapMarkers — replaces the numbered Mapbox circles in templates/nav.html with the Run
 * map's stops, run line, driver and transfer stations, drawn inside the Google map.
 *
 * The rules (colours, sizes, NEXT, grouping, draw order, the fit set) are pure functions
 * in `runMap.ts`. These components only draw them. The markers redraw on every runsheet
 * change, and only the first open moves the camera. Every marker sets its `zIndex`, so
 * the draw order in section 4 of the Run map spec never falls to Google. Marker content
 * that shouldn't take taps has `pointer-events: none`. A tap on a stop or a station goes
 * to the page, which opens the sheet.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Center, Text, useMantineTheme } from '@mantine/core';
import { AdvancedMarker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { IconRecycle } from '@tabler/icons-react';

import {
  boundsOf,
  FAN_ZOOM,
  fanOffsets,
  FIT_PADDING,
  fitPoints,
  isFanned,
  markerGroups,
  markerLook,
  markerZIndex,
  nextStopId,
  runLinePath,
  STATION_Z_INDEX,
  stationLabel,
  stopName,
  stopPosition,
  TOUCH_FLOOR,
  unitsDotLook,
  YOU_ARE_HERE_Z_INDEX,
} from './runMap';
import { useRunsheetContext, useRunsheetLocation } from '@/contexts/RunsheetContext';
import type { ReactNode } from 'react';
import type { MarkerGroup, MarkerLook, Offset } from './runMap';
import type { RunsheetLine, Station } from '@/types';

// The first open with a single point to show centres it at street zoom, because a fit
// to one point zooms all the way in.
const SINGLE_POINT_ZOOM = 17;
const RING = '0 0 0 4px var(--mantine-color-dark-9)';
// The number scales with the circle and never drops under the 12px text minimum.
const NUMBER_SIZES: Record<number, number> = { 50: 20, 44: 18, 32: 14, 28: 12 };
// Three amber pulses in about 2.5s on the stops a Move to Order # renumbered.
const PULSE_MS = 830;
const PULSE_COUNT = 3;
/** How long a Move to Order # pulse plays. */
export const PULSE_DURATION_MS = PULSE_MS * PULSE_COUNT;
const PULSE_SPREAD = 16;

// ── Component ─────────────────────────────────────────────────────────────────

/** The stops a Move to Order # renumbered. `key` changes on every move, so each one pulses. */
export interface MarkerPulse {
  ids: number[];
  key: number;
}

interface Props {
  pulse: MarkerPulse | null;
  onTapStop: (line: RunsheetLine, offset?: Offset) => void;
  onTapStation: (station: Station) => void;
}

export function RunMapMarkers({ pulse, onTapStop, onTapStation }: Props) {
  const map = useMap();
  const { runsheet, stations, currentLine } = useRunsheetContext();
  const { location } = useRunsheetLocation();
  const [zoom, setZoom] = useState<number | null>(null);

  const lines = useMemo(() => runsheet?.lines ?? [], [runsheet]);
  const driver = useMemo(
    () => (location ? { lat: location.coords.latitude, lng: location.coords.longitude } : null),
    [location],
  );
  const nextId = nextStopId(lines);
  const groups = useMemo(() => markerGroups(lines), [lines]);
  const path = useMemo(() => runLinePath(lines, driver), [lines, driver]);
  const fitSet = useMemo(() => fitPoints(lines, driver), [lines, driver]);
  const fanned = isFanned(zoom ?? map?.getZoom() ?? 0);

  // Units at one address fan out or collapse as the zoom crosses FAN_ZOOM.
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener('zoom_changed', () => setZoom(map.getZoom() ?? 0));
    return () => listener.remove();
  }, [map]);

  const renderStop = (line: RunsheetLine, offset?: Offset) => {
    const state = { nextId, selected: line.id === currentLine };
    return (
      <StopMarker
        key={line.id}
        line={line}
        look={markerLook(line, state)}
        zIndex={markerZIndex(line, state)}
        offset={offset}
        pulseKey={pulse?.ids.includes(line.id) ? pulse.key : null}
        onTap={onTapStop}
      />
    );
  };

  return (
    <>
      <FirstOpenFit points={fitSet} />
      <RunLine path={path} />
      {driver && <YouAreHere position={driver} />}
      {groups.map(group => {
        if (group.lines.length === 1) return renderStop(group.lines[0]!);
        if (fanned) return <FannedUnits key={group.key} group={group} renderStop={renderStop} />;
        return (
          <UnitsDot
            key={group.key}
            group={group}
            nextId={nextId}
            selected={group.lines.some(line => line.id === currentLine)}
          />
        );
      })}
      {stations.map(station => (
        <StationMarker key={station.id} station={station} onTap={onTapStation} />
      ))}
    </>
  );
}

// ── Camera ────────────────────────────────────────────────────────────────────

function FirstOpenFit({ points }: { points: google.maps.LatLngLiteral[] }) {
  const map = useMap();
  const fittedRef = useRef(false);

  useEffect(() => {
    if (!map || fittedRef.current || points.length === 0) return;
    const div = map.getDiv();
    const fitCamera = () => {
      fittedRef.current = true;
      if (points.length === 1) {
        map.moveCamera({ center: points[0], zoom: SINGLE_POINT_ZOOM });
        return;
      }
      map.fitBounds(boundsOf(points), FIT_PADDING);
    };
    // A fit before the map has its size puts the run off-centre, so it waits for one.
    if (div.clientWidth > 0 && div.clientHeight > 0) {
      fitCamera();
      return;
    }
    const observer = new ResizeObserver(() => {
      if (div.clientWidth === 0 || div.clientHeight === 0) return;
      observer.disconnect();
      fitCamera();
    });
    observer.observe(div);
    return () => observer.disconnect();
  }, [map, points]);

  return null;
}

// ── Run line and driver ───────────────────────────────────────────────────────

function RunLine({ path }: { path: google.maps.LatLngLiteral[] }) {
  const map = useMap();
  const maps = useMapsLibrary('maps');
  const theme = useMantineTheme();
  const lineRef = useRef<google.maps.Polyline | null>(null);
  // Google draws the line on its own canvas, so it takes a colour value, not a variable.
  const colour = theme.colors.gray[7];

  useEffect(() => {
    if (!map || !maps) return;
    // A dashed line is a solid line with no stroke and a repeated dash symbol.
    const polyline = new maps.Polyline({
      map,
      clickable: false,
      strokeOpacity: 0,
      icons: [
        {
          icon: { path: 'M 0,-1 0,1', strokeColor: colour, strokeOpacity: 1, scale: 3 },
          offset: '0',
          repeat: '12px',
        },
      ],
    });
    lineRef.current = polyline;
    return () => {
      polyline.setMap(null);
      lineRef.current = null;
    };
  }, [map, maps, colour]);

  useEffect(() => {
    lineRef.current?.setPath(path);
  }, [map, maps, colour, path]);

  return null;
}

function YouAreHere({ position }: { position: google.maps.LatLngLiteral }) {
  const haloRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const animation = haloRef.current?.animate(
      [
        { transform: 'scale(0.5)', opacity: 1 },
        { transform: 'scale(1.6)', opacity: 0 },
      ],
      { duration: 2_000, iterations: Infinity, easing: 'ease-out' },
    );
    return () => animation?.cancel();
  }, []);

  return (
    <AdvancedMarker
      position={position}
      anchorLeft="-50%"
      anchorTop="-50%"
      zIndex={YOU_ARE_HERE_Z_INDEX}
      clickable={false}
    >
      {/* Taps pass through to the stops and the map. */}
      <Center
        role="img"
        aria-label="You are here"
        w={40}
        h={40}
        pos="relative"
        style={{ pointerEvents: 'none' }}
      >
        <Box
          ref={haloRef}
          pos="absolute"
          inset={0}
          bg="blue.6"
          opacity={0.25}
          style={{ borderRadius: '50%' }}
        />
        <Box
          w={18}
          h={18}
          bg="blue.6"
          pos="relative"
          style={{
            borderRadius: '50%',
            border: '3px solid var(--mantine-color-white)',
            boxShadow: 'var(--mantine-shadow-sm)',
          }}
        />
      </Center>
    </AdvancedMarker>
  );
}

// ── Stops ─────────────────────────────────────────────────────────────────────

interface StopMarkerProps {
  line: RunsheetLine;
  look: MarkerLook;
  zIndex: number;
  /** Set while the stop pulses after a Move to Order #, and new for each move. */
  pulseKey: number | null;
  onTap: (line: RunsheetLine, offset?: Offset) => void;
  /** Where a fanned-out unit sits, in px from its address. */
  offset?: Offset;
}

function StopMarker({ line, look, zIndex, pulseKey, onTap, offset }: StopMarkerProps) {
  const position = stopPosition(line);
  if (!position) return null;
  return (
    <AdvancedMarker
      position={position}
      anchorLeft={offset ? `calc(-50% + ${offset.x}px)` : '-50%'}
      anchorTop={offset ? `calc(-50% + ${offset.y}px)` : '-50%'}
      zIndex={zIndex}
      onClick={() => onTap(line, offset)}
    >
      <Center
        role="button"
        aria-label={stopName(line)}
        w={Math.max(TOUCH_FLOOR, look.size)}
        h={Math.max(TOUCH_FLOOR, look.size)}
        pos="relative"
        style={{ cursor: 'pointer' }}
      >
        {pulseKey !== null && <PulseHalo key={pulseKey} size={look.size} />}
        <MarkerDot look={look}>{look.kind === 'unordered' ? '+' : line.displayorder}</MarkerDot>
        {look.kind === 'next' && (
          <Text
            pos="absolute"
            bottom="100%"
            fz="xs"
            fw={800}
            px={6}
            c="white"
            bg="dark.9"
            style={{ borderRadius: 4, pointerEvents: 'none' }}
          >
            NEXT
          </Text>
        )}
      </Center>
    </AdvancedMarker>
  );
}

function MarkerDot({ look, children }: { look: MarkerLook; children: ReactNode }) {
  const diamond = look.kind === 'unordered';
  // A square turned 45° is √2 wider, so the diamond's side keeps it the circle's size.
  const side = diamond ? Math.round(look.size / Math.SQRT2) : look.size;
  return (
    <Center
      data-testid="marker-dot"
      w={side}
      h={side}
      bg={`${look.colour}.8`}
      c="white"
      fz={NUMBER_SIZES[look.size] ?? 14}
      fw={800}
      style={{
        borderRadius: diamond ? 2 : '50%',
        transform: diamond ? 'rotate(45deg)' : undefined,
        border: '2px solid var(--mantine-color-white)',
        boxShadow: look.ring ? `${RING}, var(--mantine-shadow-sm)` : 'var(--mantine-shadow-sm)',
        pointerEvents: 'none',
      }}
    >
      <span style={{ transform: diamond ? 'rotate(-45deg)' : undefined }}>{children}</span>
    </Center>
  );
}

/** An amber ring that pulses out from a renumbered stop. Remounted for each move. */
function PulseHalo({ size }: { size: number }) {
  const haloRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const animation = haloRef.current?.animate(
      [
        { transform: 'scale(0.6)', opacity: 0.9 },
        { transform: 'scale(1)', opacity: 0 },
      ],
      { duration: PULSE_MS, iterations: PULSE_COUNT, easing: 'ease-out' },
    );
    return () => animation?.cancel();
  }, []);

  // An absolute child of the centred target sits in its middle.
  return (
    <Box
      ref={haloRef}
      pos="absolute"
      w={size + PULSE_SPREAD}
      h={size + PULSE_SPREAD}
      bg="yellow.5"
      opacity={0}
      style={{ borderRadius: '50%', pointerEvents: 'none' }}
    />
  );
}

// ── Units at one address ──────────────────────────────────────────────────────

interface UnitsDotProps {
  group: MarkerGroup;
  nextId: number | null;
  selected: boolean;
}

/** Below FAN_ZOOM, one dot with a count. A tap zooms in to the address so the units fan out. */
function UnitsDot({ group, nextId, selected }: UnitsDotProps) {
  const map = useMap();
  const look = unitsDotLook(group, selected);
  return (
    <AdvancedMarker
      position={group.position}
      anchorLeft="-50%"
      anchorTop="-50%"
      zIndex={markerZIndex(group.lead, { nextId, selected })}
      onClick={() => map?.moveCamera({ center: group.position, zoom: FAN_ZOOM })}
    >
      <Center
        role="button"
        aria-label={group.name}
        w={TOUCH_FLOOR}
        h={TOUCH_FLOOR}
        style={{ cursor: 'pointer' }}
      >
        <MarkerDot look={look}>×{group.lines.length}</MarkerDot>
      </Center>
    </AdvancedMarker>
  );
}

interface FannedUnitsProps {
  group: MarkerGroup;
  renderStop: (line: RunsheetLine, offset: Offset) => ReactNode;
}

/**
 * At FAN_ZOOM and above, each unit is its own marker on a ring around the address, with
 * a leader line back to it. The leader lines draw with the driver, under every stop.
 */
function FannedUnits({ group, renderStop }: FannedUnitsProps) {
  const offsets = fanOffsets(group.lines.length);
  const reach = Math.max(...offsets.map(({ x, y }) => Math.max(Math.abs(x), Math.abs(y))));
  const size = 2 * reach + 2;
  const colour = 'var(--mantine-color-gray-7)';
  return (
    <>
      <AdvancedMarker
        position={group.position}
        anchorLeft="-50%"
        anchorTop="-50%"
        zIndex={YOU_ARE_HERE_Z_INDEX}
        clickable={false}
      >
        <svg
          width={size}
          height={size}
          viewBox={`${-size / 2} ${-size / 2} ${size} ${size}`}
          style={{ display: 'block', pointerEvents: 'none' }}
        >
          {offsets.map(({ x, y }, i) => (
            <line key={i} x1={0} y1={0} x2={x} y2={y} stroke={colour} strokeWidth={2} />
          ))}
          <circle
            r={5}
            fill="var(--mantine-color-dark-9)"
            stroke="var(--mantine-color-white)"
            strokeWidth={2}
          />
        </svg>
      </AdvancedMarker>
      {group.lines.map((line, i) => renderStop(line, offsets[i]))}
    </>
  );
}

// ── Stations ──────────────────────────────────────────────────────────────────

function StationMarker({
  station,
  onTap,
}: {
  station: Station;
  onTap: (station: Station) => void;
}) {
  const position = stopPosition(station);
  if (!position) return null;
  return (
    <AdvancedMarker
      position={position}
      anchorLeft="-50%"
      anchorTop="-50%"
      zIndex={STATION_Z_INDEX}
      onClick={() => onTap(station)}
    >
      <Center
        role="button"
        aria-label={station.name}
        w={TOUCH_FLOOR}
        h={TOUCH_FLOOR}
        bg="dark.7"
        c="white"
        pos="relative"
        style={{
          borderRadius: 8,
          border: '2px solid var(--mantine-color-white)',
          boxShadow: 'var(--mantine-shadow-sm)',
          cursor: 'pointer',
        }}
      >
        <IconRecycle size={24} />
        <Text
          pos="absolute"
          left="100%"
          ml={4}
          fz="xs"
          fw={700}
          px={4}
          c="dark.9"
          bg="white"
          style={{ borderRadius: 4, whiteSpace: 'nowrap', pointerEvents: 'none' }}
        >
          {stationLabel(station.name)}
        </Text>
      </Center>
    </AdvancedMarker>
  );
}
